import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BanknotesIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  EyeIcon,
  CalendarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const InvoiceDashboard = ({ onTabChange }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalPartners: 0,
    activePartners: 0,
    totalInvoices: 0,
    draftInvoices: 0,
    sentInvoices: 0,
    partialInvoices: 0,
    paidInvoices: 0,
    overdueInvoices: 0,
    totalRevenue: 0,
    totalPaymentsReceived: 0,
    totalOutstanding: 0,
    partialPayments: 0,
    averageInvoice: 0
  });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      // Fetch dashboard stats
      const statsResponse = await fetch('/api/finance/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch recent invoices
      const invoicesResponse = await fetch('/api/finance/invoices?limit=5', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statsResponse.ok && invoicesResponse.ok) {
        const statsData = await statsResponse.json();
        const invoicesData = await invoicesResponse.json();
        
        setStats(statsData);
        setRecentInvoices(invoicesData.invoices || []);
      } else {
        throw new Error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setMessage({ type: 'error', text: t('invoiceDashboard.failedToLoadDashboardData') });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Draft':
        return 'bg-gray-300 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Paid':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'Sent':
        return <ClockIcon className="h-4 w-4" />;
      case 'Partial':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'Overdue':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'Draft':
        return <DocumentTextIcon className="h-4 w-4" />;
      default:
        return <DocumentTextIcon className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount, currency = 'BGN') => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  // Calculate effective status (including overdue)
  const getEffectiveStatus = (invoice) => {
    if (invoice.status === 'Paid' || invoice.status === 'Draft' || invoice.status === 'Cancelled' || invoice.status === 'Partial') {
      return invoice.status;
    }
    if (invoice.status === 'Sent' && new Date(invoice.dueDate) < new Date()) {
      return 'Overdue';
    }
    return invoice.status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('invoiceDashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <span>{message.text}</span>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Partners */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('invoiceDashboard.totalPartners')}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.totalPartners}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-sm text-gray-600">
                <span className="text-green-600 font-medium">
                  {stats.activePartners}
                </span>{' '}
                {t('invoiceDashboard.active')}
              </div>
            </div>
          </div>

          {/* Total Invoices */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('invoiceDashboard.totalInvoices')}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.totalInvoices}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-sm text-gray-600">
                <span className="text-green-600 font-medium">
                  {stats.paidInvoices}
                </span>{' '}
                {t('invoiceDashboard.paid')}
              </div>
            </div>
          </div>

          {/* Sent Invoices */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('invoiceDashboard.sentInvoices')}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.sentInvoices}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-sm text-gray-600">
                <span className="text-red-600 font-medium">
                  {stats.overdueInvoices}
                </span>{' '}
                {t('invoiceDashboard.overdue')}
              </div>
            </div>
          </div>

          {/* Total Payments Received */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BanknotesIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('invoiceDashboard.totalPaymentsReceived')}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(stats.totalPaymentsReceived)}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-sm text-gray-600">
                <span className="text-green-600 font-medium">
                  {t('invoiceDashboard.fromAllInvoices')}
                </span>
              </div>
            </div>
          </div>


        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Invoices */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">{t('invoiceDashboard.recentInvoices')}</h3>
                    <button 
                      onClick={() => window.location.href = '/admin/dashboard/invoices?tab=list'}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      {t('invoiceDashboard.viewAll')}
                    </button>
                  </div>
              </div>
              <div className="divide-y divide-gray-200">
                {recentInvoices.length === 0 ? (
                  <div className="px-6 py-8 text-center">
                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">{t('invoiceDashboard.noInvoicesYet')}</p>
                    <button 
                      onClick={() => window.location.href = '/admin/dashboard/invoices/create'}
                      className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                      {t('invoiceDashboard.createFirstInvoice')}
                    </button>
                  </div>
                ) : (
                  recentInvoices.map((invoice) => (
                    <div key={invoice._id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </p>
                            <p className="text-sm text-gray-500">
                              {invoice.partnerId?.name || t('invoiceDashboard.unknownPartner')}
                            </p>
                            <p className="text-xs text-gray-400">
                              {t('invoiceDashboard.due')}: {formatDate(invoice.dueDate)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(invoice.totalAmount, invoice.currency)}
                            </p>
                            <div className="flex items-center justify-end space-x-1">
                              {getStatusIcon(getEffectiveStatus(invoice))}
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(getEffectiveStatus(invoice))}`}>
                                {t(`invoiceDashboard.status${getEffectiveStatus(invoice)}`)}
                              </span>
                            </div>
                          </div>
                          <button 
                            onClick={() => window.location.href = `/admin/dashboard/invoices/${invoice._id}`}
                            className="text-gray-400 hover:text-gray-600"
                            title={t('invoiceDashboard.viewInvoiceDetails')}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions & Charts */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoiceDashboard.quickActions')}</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => window.location.href = '/admin/dashboard/invoices/create'}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>{t('invoiceDashboard.createInvoice')}</span>
                </button>
                <button 
                  onClick={() => onTabChange && onTabChange('partners')}
                  className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <UserGroupIcon className="h-5 w-5" />
                  <span>{t('invoiceDashboard.addPartner')}</span>
                </button>
                <button 
                  onClick={() => onTabChange && onTabChange('list')}
                  className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <ChartBarIcon className="h-5 w-5" />
                  <span>{t('invoiceDashboard.viewAllInvoices')}</span>
                </button>
              </div>
            </div>

            {/* Invoice Status Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoiceDashboard.invoiceStatus')}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="text-sm text-gray-600">{t('invoiceDashboard.draft')}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {stats.draftInvoices}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">{t('invoiceDashboard.sent')}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {stats.sentInvoices}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">{t('invoiceDashboard.partial')}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {stats.partialInvoices}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">{t('invoiceDashboard.paid')}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {stats.paidInvoices}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">{t('invoiceDashboard.overdue')}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {stats.overdueInvoices}
                  </span>
                </div>
              </div>
            </div>


          </div>
        </div>


    </div>
  );
};

export default InvoiceDashboard; 